import React, { useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router'
import { ChecklistItem, Goal } from 'Generated/graphql-types'
import { analytics } from 'Modules/Analytics'
import { useAppState } from 'Context/AppContext/AppContext'
import { useQuery } from '@apollo/client'
import { GOAL_UPDATES } from 'Apollo/Queries/GoalUpdates'
import { goals } from 'Modules/Analytics/trackActionInterface/Impact/Goals'
import {
  Details,
  GoalSectionsContainer,
  MenuTab,
  MenuTabs,
} from './GoalSections.styled'
import { GoalSectionView } from './GoalSectionView'
import { GoalDashboardTab, GoalSectionParamOptions } from '../types'

type GoalSectionTabsProps = {
  goal: Goal
}

export const SECTION_PARAM_KEY = 'section'

export const GoalSections = ({ goal }: GoalSectionTabsProps) => {
  const history = useHistory()
  const location = useLocation()
  const {
    nonprofitOrgConfig: { platformPlan, role, orgId: nonprofitOrgId },
  } = useAppState()

  const { data: { goalUpdates = [] } = {}, loading: goalUpdatesLoading } =
    useQuery(GOAL_UPDATES, {
      variables: {
        nonprofitOrgId,
        goalId: goal.id,
      },
    })

  const sectionParam = new URLSearchParams(location.search).get(
    SECTION_PARAM_KEY
  ) as GoalSectionParamOptions
  const [visibleSection, setVisibleSection] = useState<GoalSectionParamOptions>(
    sectionParam || 'tasks'
  )
  const tasks = goal?.ChecklistItem as unknown as ChecklistItem[]

  const goalsDashboardTabs: GoalDashboardTab[] = useMemo(
    () => [
      { count: tasks.length, label: 'Tasks', param: 'tasks' },
      {
        label: 'Chart',
        param: 'progress',
      },
      {
        count: Number(goalUpdates.nodes?.length) || 0,
        label: 'Progress Updates',
        param: 'updates' as GoalSectionParamOptions,
      },
    ],
    [tasks.length, goalUpdates.nodes?.length]
  )

  // Set visible section based on query param, if valid
  useEffect(() => {
    const validSections = goalsDashboardTabs.map((tab) => tab.param)
    const currentSection = new URLSearchParams(location.search).get(
      SECTION_PARAM_KEY
    ) as GoalSectionParamOptions
    if (validSections.includes(currentSection))
      setVisibleSection(currentSection)
  }, [goalsDashboardTabs, location.search])

  const handleTabChange = (tab: string) => {
    const searchParams = new URLSearchParams(location.search)
    searchParams.set(SECTION_PARAM_KEY, tab)
    history.replace({ search: searchParams.toString() })
    const eventParams = { goalId: goal.id, userAccountType: role, platformPlan }

    switch (tab) {
      case 'tasks':
        analytics.trackActionCreator(goals.Tab_Tasks_Clicked)(eventParams)
        break
      case 'comments':
        analytics.trackActionCreator(goals.Tab_Comments_Clicked)(eventParams)
        break
      case 'progress':
        analytics.trackActionCreator(goals.Tab_Charts_Clicked)(eventParams)
        break
      default:
        break
    }
  }

  const menuTabs = goalsDashboardTabs.map((tab) => (
    <MenuTab
      key={tab.label}
      label={`${tab.label} ${tab.count ? `(${tab.count})` : ''}`}
      value={tab.param}
    />
  ))

  return (
    <GoalSectionsContainer>
      <MenuTabs
        onChange={(_, value) => handleTabChange(value)}
        orientation='horizontal'
        value={visibleSection}
      >
        {menuTabs}
      </MenuTabs>
      <Details>
        <GoalSectionView
          goal={goal}
          visibleSection={visibleSection}
          tasks={tasks}
          updates={goalUpdates.nodes}
          updatesLoading={goalUpdatesLoading}
        />
      </Details>
    </GoalSectionsContainer>
  )
}
